import React, { useState } from 'react'
import ReadinessCheckListItem from './ReadinessCheckListItem'
import Button from '../../../components/Form/Button'
import VNEMWarning from './VNEMWarning'
import { DetailedProgramItem, ReadinessChecklist } from '../../../apis/types'
import { object, string } from 'yup'
import { useFormik } from 'formik'
import { linkUtilityPortalApi, updateUtilityPortalCredentialsApi, updateScraperActiveFlags } from '../../../apis/apis'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import UpdateCredentialsForm from './UpdateCredentialsForm'
import Modal from '../../../components/Modal'
import { useAppDispatch, useAppSelector } from '../../../shared/redux/hooks'
import ControlledToggle from '../../../components/Form/ControlledToggle'
import classnames from 'classnames'
import VNEMAllocationManager from './VNEMAllocationManager'
import { formatReadableDate } from '../../../util/string'
import IntervalDataChecklistItem from './IntervalDataChecklistItem'
import { getScraperShortNameValidationError, normalizeScraperShortName } from '../../../util/scraperShortNameValidation'
import ProgramGeneratingMetersPanel from './ProgramGeneratingMetersPanel'

const scraperShortNameFieldSchema = string().test('scraper-short-name', function validateScraperShortNameField(value) {
  const validationError = getScraperShortNameValidationError(value ?? '')
  if (validationError == null) {
    return true
  }
  return this.createError({ message: validationError })
})

interface ReadinessCheckListProps {
  program: DetailedProgramItem | undefined
  readinessChecklist: ReadinessChecklist
  handleCaptureVNEMSubmission: () => void
  handleRefreshReadinessChecklist: () => void
  handleRefreshIntervalDataReadinessChecklist: () => void
}

const ReadinessCheckList: React.FC<ReadinessCheckListProps> = ({
  program,
  readinessChecklist,
  handleCaptureVNEMSubmission,
  handleRefreshReadinessChecklist,
  handleRefreshIntervalDataReadinessChecklist,
}) => {
    const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.app)

  const [showUpdateCredentialsForm, setShowUpdateCredentialsForm] = useState(false)
  const [confirmModal, setConfirmModal] = useState<React.ReactElement | boolean>(false)

  const programId = program?.id

  // TODO(WEB-7242): 'program:manager:meters:modify' / 'program:manager:generating-meters:modify' /
  // 'program:manager:vnem:modify' don't exist in Auth0 yet, so no real user has them. Hardcoded
  // to true for now so the mocked Program Meters (merged into the VNEM table) / Generating Meters
  // / VNEM percentage-edit UI is visible and demoable; switch back to permissions.includes(...)
  // once the scopes are rolled out.
  const canModifyMeters = true || permissions?.includes('program:manager:meters:modify')
  const canModifyGeneratingMeters = true || permissions?.includes('program:manager:generating-meters:modify')
  const canModifyVnem = true || permissions?.includes('program:manager:vnem:modify')

  const handleToggleScraperActiveFlags = async ({
    isScraperActive,
    isBillProcessingActive,
  }: {
    isScraperActive?: boolean
    isBillProcessingActive?: boolean
  }): Promise<void> => {
    if (program?.id == null) return
    try {
      await new Promise((resolve, reject) => {
        setConfirmModal(<Modal resolve={resolve} reject={reject} />)
      })
      setConfirmModal(false)
      updateScraperActiveFlags(program?.id, isScraperActive, isBillProcessingActive)
        .then(() => {
          dispatch(
            pushToast({
              type: 'success',
              message: isScraperActive != null ? 'Scraper active status updated successfully' : 'Bill processing active status updated successfully',
            })
          )
          handleRefreshReadinessChecklist()
        })
        .catch(
          async (e) =>
            await dispatch(
              pushToast({
                type: 'error',
                message: isScraperActive != null ? 'Error updating scraper active status' : 'Error updating bill processing active status',
                description: `${e.response?.data?.error !== undefined ? `${e.response?.data?.error as string}:` : 'Error:'} ${e.message as string}`,
              })
            )
        )
    } catch (e) {
      setConfirmModal(false)
    }
  }

  const requireStr = 'This field is required'
  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
      shortName: '',
    },
    validationSchema: object().shape({
      username: string().required(requireStr).min(8, 'Username must be at least 8 characters'),
      password: string().required(requireStr).min(8, 'Password must be at least 8 characters'),
      shortName: readinessChecklist.portalLinking.isLinked
        ? string()
        : scraperShortNameFieldSchema,
    }),
    onSubmit: async (values) => {
      const linkPortal = async (): Promise<void> => {
        let res
        if (readinessChecklist.portalLinking.isLinked) {
          res = await updateUtilityPortalCredentialsApi(program?.id ?? '', {
            username: values.username,
            password: values.password,
          })
        } else {
          res = await linkUtilityPortalApi(program?.id ?? '', {
            username: values.username,
            password: values.password,
            shortName: normalizeScraperShortName(values.shortName),
          })
        }
        if (res.success !== true) {
          throw new Error('Error linking utility portal')
        }
      }

      try {
        await new Promise((resolve, reject) => {
          setConfirmModal(<Modal resolve={resolve} reject={reject} />)
        })
        setConfirmModal(false)
        await linkPortal()
          .then(async () => {
            await dispatch(
              pushToast({
                type: 'success',
                message: 'Utility portal linked successfully',
              })
            )
            setShowUpdateCredentialsForm(false)
            handleRefreshReadinessChecklist()
            handleRefreshIntervalDataReadinessChecklist()
          })
          .catch(async (e) => {
            await dispatch(
              pushToast({
                type: 'error',
                message: 'Error fetching file list',
                description: `${e.response?.data?.error !== undefined ? `${e.response?.data?.error as string}:` : 'Error:'} ${e.message as string}`,
              })
            )
          })
      } catch (e) {
        setConfirmModal(false)
      }
    },
  })

  React.useEffect(() => {
    setShowUpdateCredentialsForm(false)
    void formik.resetForm({
      values: {
        username: '',
        password: '',
        shortName: '',
      },
    })
    // Reset link credentials when navigating to a different program.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId])

  const handleOpenLinkForm = (): void => {
    setShowUpdateCredentialsForm(true)
  }

  return (
    <>
      {confirmModal}
      <div className="mb-5 text-xl font-medium dark:text-white">Readiness Checklist</div>
      <div className="flex flex-col justify-between">
        <div className="border-b border-slate-300 dark:border-slate-600 pb-4">
          {readinessChecklist.portalLinking.isLinked ? (
            <ReadinessCheckListItem
              isComplete={true}
              isActive={false}
              isIncomplete={false}
              headingText={`${program?.utility != null ? program?.utility : 'Utility'} Portal Linked`}
              infoComponent={
                <>
                  <p>Username: {readinessChecklist.portalLinking.utilityUsername}</p>
                  {readinessChecklist.portalLinking.shortName != null && (
                    <p>Short name: {readinessChecklist.portalLinking.shortName}</p>
                  )}
                  {readinessChecklist.portalLinking.scraperLastRun != null && (
                    <p>Scraper job last run: {formatReadableDate(readinessChecklist.portalLinking.scraperLastRun, false)}</p>
                  )}
                  <div className="ml-4 flex flex-col justify-between">
                    <ControlledToggle
                      label="Scraper Active"
                      className="mt-4"
                      onChange={() => {
                        handleToggleScraperActiveFlags({ isScraperActive: !readinessChecklist.portalLinking.isScraperActive })
                      }}
                      checked={readinessChecklist.portalLinking.isScraperActive ?? false}
                    />
                    <ControlledToggle
                      label="Bill Processing Active"
                      className={classnames('mt-4', { hidden: !readinessChecklist.portalLinking.isScraperActive })}
                      onChange={() => {
                        handleToggleScraperActiveFlags({ isBillProcessingActive: !readinessChecklist.portalLinking.isBillProcessingActive })
                      }}
                      checked={readinessChecklist.portalLinking.isBillProcessingActive ?? false}
                    />
                  </div>
                </>
              }
              actionComponent={
                <>
                  <div className="flex flex-row justify-between">
                    <div className="flex flex-col justify-between">
                      <UpdateCredentialsForm
                        formik={formik}
                        showUpdateCredentialsForm={showUpdateCredentialsForm}
                        setShowUpdateCredentialsForm={setShowUpdateCredentialsForm}
                      />
                    </div>
                  </div>
                </>
              }
            />
          ) : (
            <ReadinessCheckListItem
              isComplete={false}
              isActive={false}
              isIncomplete={true}
              headingText={`${program?.utility != null ? program?.utility : 'Utility'} Portal Linked`}
              infoComponent={<p>{`${program?.utility != null ? program?.utility : 'Utility'} portal not linked.`}</p>}
              actionComponent={
                <UpdateCredentialsForm
                  formik={formik}
                  showUpdateCredentialsForm={showUpdateCredentialsForm}
                  setShowUpdateCredentialsForm={setShowUpdateCredentialsForm}
                  showShortNameField={true}
                  linkMode={true}
                  onOpenForm={handleOpenLinkForm}
                />
              }
            />
          )}
        </div>

        {/* SMD Enrollment Section */}
        <div className="border-b border-slate-300 dark:border-slate-600 py-4">
          {readinessChecklist.intervalDataStatus != null ? (
            <IntervalDataChecklistItem
              program={program}
              readinessChecklist={readinessChecklist}
              handleRefreshReadinessChecklist={handleRefreshReadinessChecklist}
            />
          ) : (
            <div>Loading readiness checklist item...</div>
          )}
        </div>

        <div className="pt-4">
          {/* If a program does not have a salesforce id, even if the readiness checklist says VNEM allocations have been submitted, don't
          let the user capture new VNEM allocations or view them  */}
          {readinessChecklist.allocationSubmission.isSubmitted && program?.salesforceId != null ? (
            <ReadinessCheckListItem
              isComplete={true}
              isActive={false}
              isIncomplete={false}
              headingText="VNEM Allocation Submission Captured"
              infoComponent={
                <>
                  {readinessChecklist.allocationSubmission.submissionDate != null && (
                    <p>Submission date: {formatReadableDate(readinessChecklist.allocationSubmission.submissionDate, true)}</p>
                  )}
                </>
              }
              actionComponent={
                <>
                  <Button
                    onClick={() => {
                      handleCaptureVNEMSubmission()
                    }}
                  >
                    Capture VNEM Allocations...
                  </Button>
                  <Button className="mt-2">
                    <a
                      href={`https://metabase.it.kingenergy.com/question/76-onebill-billing-readiness-report?property_name=${program?.name?.split(' ')[0] ?? ''}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium"
                    >
                      View Readiness Report...
                    </a>
                  </Button>
                </>
              }
            />
          ) : (
            <ReadinessCheckListItem
              isComplete={false}
              isActive={false}
              isIncomplete={true}
              headingText="VNEM Allocation Submission Captured"
              infoComponent={<p>No submissions captured</p>}
              actionComponent={
                <>
                  {program?.salesforceId != null ? (
                    <Button
                      className="mt-2"
                      onClick={() => {
                        handleCaptureVNEMSubmission()
                      }}
                    >
                      Capture VNEM Allocations...
                    </Button>
                  ) : (
                    <div className="ml-12">
                      <VNEMWarning type="error" text="This program must be assigned a Salesforce Program ID in order to capture VNEM allocations" />
                    </div>
                  )}
                </>
              }
            />
          )}
        </div>
      </div>

      {/* Program onboarding: generating meters (WEB-7356). Program Meters (WEB-7247) is merged
          into the VNEM allocations table below and Utilities/Taxes (WEB-7248) moved to the
          property screen — see PropertyDetailPanels on the Tenants page. */}
      {program?.id != null && (
        <div className="mt-6 space-y-5">
          <ProgramGeneratingMetersPanel programId={program.id} canModify={canModifyGeneratingMeters} />
        </div>
      )}

      {/* VNEM Allocation Management Section (includes merged Program Meters dates) */}
      {program?.id && (
        <div className="mt-6">
          <VNEMAllocationManager
            programId={program.id}
            onRefresh={handleRefreshReadinessChecklist}
            canModifyMeters={canModifyMeters}
            canModifyVnem={canModifyVnem}
          />
        </div>
      )}
    </>
  )
}

export default ReadinessCheckList
